import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaSearch, FaFilter, FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import { bookingService } from '../services/api';
import '../styles/Bookings.css';

function BookingsList() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const response = await bookingService.getAll({ limit: 1000 });
      setBookings(response.bookings || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Buchung wirklich löschen?')) return;
    
    try {
      await bookingService.delete(id);
      setBookings(bookings.filter(b => b.id !== id));
    } catch (error) {
      alert('Fehler beim Löschen: ' + error.message);
    }
  };

  // Filter & Sort
  const filteredBookings = bookings
    .filter(booking => {
      const matchesSearch = 
        booking.Nachname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.Vorname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking['E-Mail']?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.Ort?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || booking.Status === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch(sortBy) {
        case 'date':
          return new Date(b.Reservierung_ablauf || 0) - new Date(a.Reservierung_ablauf || 0);
        case 'name':
          return (a.Nachname || '').localeCompare(b.Nachname || '');
        case 'price':
          return (b.Preis || 0) - (a.Preis || 0);
        default:
          return 0;
      }
    });

  const getStatusColor = (status) => {
    const statusMap = {
      'Neu': 'neu',
      'Angefragt': 'angefragt',
      'Bestätigt': 'bestaetigt',
      'Versendet': 'versendet',
      'Zurück': 'zurueck',
      'Abgeschlossen': 'abgeschlossen',
      'Bezahlt': 'bestaetigt'
    };
    return statusMap[status] || 'neu';
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="bookings-page">
      <div className="page-header">
        <div>
          <h1>📦 Feierpakete</h1>
          <p className="text-gray">Alle Feierpaket-Buchungen im Überblick</p>
        </div>
        <Link to="/feierpakete/new" className="btn btn-primary">
          <FaPlus /> Neue Buchung
        </Link>
      </div>

      {/* Filters */}
      <div className="filters-bar card">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Suche nach Name, E-Mail, Ort..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <FaFilter />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Alle Status</option>
            <option value="Neu">Neu</option>
            <option value="Angefragt">Angefragt</option>
            <option value="Bestätigt">Bestätigt</option>
            <option value="Versendet">Versendet</option>
            <option value="Zurück">Zurück</option>
            <option value="Abgeschlossen">Abgeschlossen</option>
            <option value="Bezahlt">Bezahlt</option>
          </select>
        </div>

        <div className="filter-group">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date">Sortieren: Datum</option>
            <option value="name">Sortieren: Name</option>
            <option value="price">Sortieren: Preis</option>
          </select>
        </div>

        <div className="results-count">
          {filteredBookings.length} Buchungen
        </div>
      </div>

      {/* Bookings Table */}
      {filteredBookings.length === 0 ? (
        <div className="card text-center" style={{ padding: '3rem' }}>
          <p className="text-gray">Noch keine Buchungen vorhanden.</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kunde</th>
                  <th>E-Mail</th>
                  <th>Telefon</th>
                  <th>Ort</th>
                  <th>Preis</th>
                  <th>Status</th>
                  <th>Versand</th>
                  <th>Reservierung</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map(booking => (
                  <tr key={booking.id}>
                    <td>
                      <strong>{booking.Vorname} {booking.Nachname}</strong>
                    </td>
                    <td>{booking['E-Mail']}</td>
                    <td>{booking.Telefon}</td>
                    <td>{booking.Ort}</td>
                    <td className="text-primary">
                      <strong>{booking.Preis ? `${booking.Preis}€` : '-'}</strong>
                    </td>
                    <td>
                      <span className={`status-badge status-${getStatusColor(booking.Status)}`}>
                        {booking.Status || 'Neu'}
                      </span>
                    </td>
                    <td>
                      {booking.Versanddatum ? 
                        new Date(booking.Versanddatum).toLocaleDateString('de-DE') : 
                        '-'
                      }
                    </td>
                    <td>
                      {booking.Reservierung_ablauf ? 
                        new Date(booking.Reservierung_ablauf).toLocaleDateString('de-DE') : 
                        '-'
                      }
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Link 
                          to={`/feierpakete/${booking.id}`} 
                          className="btn-icon"
                          title="Details"
                        >
                          <FaEye />
                        </Link>
                        <Link 
                          to={`/feierpakete/${booking.id}/edit`} 
                          className="btn-icon"
                          title="Bearbeiten"
                        >
                          <FaEdit />
                        </Link>
                        <button 
                          onClick={() => handleDelete(booking.id)}
                          className="btn-icon btn-danger"
                          title="Löschen"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingsList;
